/**
 * sceneConstellation — a living star map that demonstrates the NSPP
 * pipeline at full power: SDF circles + per-node filter chain +
 * mix-blend-mode (additive blending) + backdrop-blur + gradient fills +
 * per-node velocity-aware motion blur, all composed in a single scene.
 *
 * The visualization:
 *  • 180 nodes drift across the viewport at per-node velocities, wrapped
 *    softly at the edges so the cloud feels infinite.
 *  • Each node has mix-blend-mode: screen so overlapping circles add
 *    luminance — the "core" of bright clusters gets visually whiter,
 *    something vanilla CSS compositing can't reproduce without an alpha
 *    clamp trick (and which a 2D canvas can't reproduce at all because
 *    it doesn't have a per-pixel pipeline that respects blend modes the
 *    way WebGL does).
 *  • Lines connect nearest neighbours (k-nearest, k=2). Each line is a
 *    long thin gradient rect with backdrop-filter blur so it bleeds
 *    into the underlying color as it moves.
 *  • Hovering a node with Edit ON lights it up (brightness/saturate
 *    ramp). Click locks onto it and the whole cluster orbits slowly
 *    around the locked node.
 *
 * Designed to be the "wow, only NSPP can do this" demo.
 */
import { addNode, createScene, makeRect, makeText } from "../sceneGraph";
import type { Scene, SceneNode } from "../sceneGraph";

interface Star {
  x: number; y: number;
  vx: number; vy: number;
  size: number;
  hue: number;       // 0..1 — drives a HSL-style gradient between two palette stops
  freq: number;      // for breathing
  phase: number;     // for breathing
}

interface Line {
  a: number;         // star index A
  b: number;         // star index B
  width: number;     // visual thickness
}

export function sceneConstellation(w: number, h: number): Scene {
  const scene = createScene();
  const z = { v: 0 };
  const tick = (): number => z.v++; // monotonic counter, used as zIndex

  // Palette — two warm + two cool endpoints. mix-blend-mode: screen will
  // merge them toward white in dense regions (the same effect real
  // photographers get when overlaying star exposures).
  const palette: Array<[number, number, number]> = [
    [0.45, 0.85, 1.00],  // ice blue
    [0.95, 0.55, 0.85],  // rose
    [0.98, 0.78, 0.35],  // amber
    [0.55, 0.45, 1.00],  // violet
  ];
  const pickColor = (t: number): [number, number, number] => {
    const i = Math.floor(t * palette.length) % palette.length;
    const a = palette[i]!;
    const b = palette[(i + 1) % palette.length]!;
    return [a[0] * 0.6 + b[0] * 0.4, a[1] * 0.6 + b[1] * 0.4, a[2] * 0.6 + b[2] * 0.4];
  };

  // Seeded RNG so the constellation looks the same every reload — easier
  // to appreciate in motion when the structure is stable.
  let seed = 0x1337beef;
  const rand = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };

  // ------- Stars ----------------------------------------------------------
  const N = 180;
  const stars: Star[] = [];
  for (let i = 0; i < N; i++) {
    // Soft-edge wrap region (10% margin inside the viewport) so stars
    // can drift through without disappearing at the boundary.
    const x = w * 0.1 + rand() * w * 0.8;
    const y = h * 0.1 + rand() * h * 0.8;
    // Per-star velocity, scaled so the slowest stars barely move and the
    // fastest ones glide. Velocity is what makes motion-blur kick in
    // visually when NSPP's velocity-buffer pipeline samples deltas.
    const speed = 4 + rand() * 28;
    const angle = rand() * Math.PI * 2;
    const size = 4 + rand() * 14;
    stars.push({
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size,
      hue: rand(),
      freq: 0.4 + rand() * 1.2,
      phase: rand() * Math.PI * 2,
    });
  }

  // ------- Background plate -----------------------------------------------
  // Pure black with a subtle radial glow so the additive blend has
  // something warm to land on. This is the only place we draw fill =
  // black; everything above it is bright + screen-blended.
  const bgColor = { r: 0.04, g: 0.04, b: 0.07, a: 1 };
  addNode(scene, makeRect({
    x: 0, y: 0, w, h,
    fill: bgColor,
  })); z.v++;

  // Faint deep-color radial behind the constellation — gives the
  // cluster a sense of depth (cool blue at center, near-black at edges).
  // We fake a radial with 5 stacked rectangles whose fills lighten in
  // steps. Cheap and only 5 nodes.
  for (let i = 5; i >= 1; i--) {
    const t = i / 5;
    const rw = w * t;
    const rh = h * t;
    addNode(scene, makeRect({
      x: (w - rw) / 2, y: (h - rh) / 2, w: rw, h: rh,
      radius: Math.min(rw, rh) / 2,
      fill: { r: 0.06 + (1 - t) * 0.02, g: 0.06 + (1 - t) * 0.02, b: 0.10 + (1 - t) * 0.04, a: 0.18 },
      mixBlendMode: "screen",
    })); z.v++;
  }

  // ------- Per-star SDF circles ------------------------------------------
  // Each circle is a `makeRect` with a gradient fill and a per-node
  // `update` that handles drift, breathing, and re-wraps at the edges.
  // The mix-blend-mode: screen is what creates the photographic
  // over-exposure effect when stars cluster.
  const starNodes: SceneNode[] = [];
  for (let i = 0; i < stars.length; i++) {
    const s = stars[i]!;
    const color = pickColor(s.hue);
    const node = makeRect({
      x: s.x - s.size / 2,
      y: s.y - s.size / 2,
      w: s.size,
      h: s.size,
      radius: s.size / 2,
      fill: { r: color[0], g: color[1], b: color[2], a: 0.9 },
      gradientEnd: { r: color[0] * 0.4, g: color[1] * 0.4, b: color[2] * 0.4, a: 0 },
      gradientAngle: Math.PI * (s.hue * 2 - 1),
      mixBlendMode: "screen",
      // Filter chain — saturate + brightness so the colour stays vivid
      // even after blend-mode dampens the highlight. This is the
      // "alive and lit" recipe in NSPP.
      filter: { blur: 0, brightness: 1.15, contrast: 1.05, saturate: 1.6, hueRotate: 0, invert: 0 },
    });
    node.update = (n: SceneNode, t: number) => {
      // Per-frame drift + edge wrap. We sample at dt granularity by
      // remembering the last t we drew at.
      const lastT = (n as { _lastT?: number })._lastT ?? t;
      const dt = Math.max(0, Math.min(0.05, t - lastT)); // clamp dt for tab-switch resumes
      (n as { _lastT?: number })._lastT = t;
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      // Soft wrap — when a star exits one side, it re-enters the opposite.
      // The 10% margin keeps the motion feeling comfortable.
      if (s.x < w * -0.05) s.x += w * 1.1;
      if (s.x > w * 1.05) s.x -= w * 1.1;
      if (s.y < h * -0.05) s.y += h * 1.1;
      if (s.y > h * 1.05) s.y -= h * 1.1;
      // Breathing scale (every star's pulse is independent).
      const breath = 0.85 + 0.15 * Math.sin(t * s.freq + s.phase);
      const sw = s.size * breath;
      const sh = s.size * breath;
      n.x = s.x - sw / 2;
      n.y = s.y - sh / 2;
      n.w = sw;
      n.h = sh;
    };
    addNode(scene, node); z.v++;
    starNodes.push(node);
  }

  // ------- Connector lines (k-nearest, k=2) -------------------------------
  // For each star, find the two closest neighbours. Build an undirected
  // edge set, then render each edge as a thin gradient rect with a
  // large backdropBlur so it smears into the surrounding pixels as it
  // moves. This is the "energy beam" look that NSPP's per-node filter
  // + backdrop-blur makes trivially.
  const kNearest = 2;
  const seen = new Set<string>();
  const edges: Line[] = [];
  for (let i = 0; i < stars.length; i++) {
    const a = stars[i]!;
    const dists: Array<{ j: number; d: number }> = [];
    for (let j = 0; j < stars.length; j++) {
      if (i === j) continue;
      const b = stars[j]!;
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      dists.push({ j, d: dx * dx + dy * dy });
    }
    dists.sort((u, v) => u.d - v.d);
    for (let n = 0; n < kNearest && n < dists.length; n++) {
      const j = dists[n]!.j;
      const key = i < j ? `${i}-${j}` : `${j}-${i}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const width = 0.5 + Math.sqrt(dists[n]!.d) / 600;
      edges.push({ a: i, b: j, width });
    }
  }

  // One SDF rect per edge. The update hook positions it between the two
  // endpoint stars, scaled + rotated to match. Backdrop-blur smears the
  // edge against the underlying stars for a glow effect.
  for (const edge of edges) {
    const A = stars[edge.a]!;
    const B = stars[edge.b]!;
    const dx = B.x - A.x;
    const dy = B.y - A.y;
    const len = Math.sqrt(dx * dx + dy * dy);
    const ang = Math.atan2(dy, dx);
    const cx = (A.x + B.x) / 2;
    const cy = (A.y + B.y) / 2;
    const color = pickColor((A.hue + B.hue) * 0.5);
    const node = makeRect({
      // Position rect at center, then offset to make (0,0) the midpoint
      x: cx - len / 2,
      y: cy - edge.width / 2,
      w: len,
      h: edge.width,
      // The rect pipeline rotates the rect about its center via
      // rotation + transformOrigin. Make rotation match atan2(dy,dx).
      rotation: ang,
      transformOriginX: 0.5,
      transformOriginY: 0.5,
      fill: { r: color[0], g: color[1], b: color[2], a: 0.55 },
      // Backdrop blur is what makes the line glow into its surroundings.
      backdropBlur: 6,
      mixBlendMode: "screen",
    });
    node.update = (n: SceneNode) => {
      // Re-read endpoints — stars drift each frame, so recompute every
      // tick. Avoid recomputing from old A/B — pull the live positions.
      const a = stars[edge.a]!;
      const b = stars[edge.b]!;
      const dx2 = b.x - a.x;
      const dy2 = b.y - a.y;
      const len2 = Math.sqrt(dx2 * dx2 + dy2 * dy2);
      const ang2 = Math.atan2(dy2, dx2);
      const cx2 = (a.x + b.x) / 2;
      const cy2 = (a.y + b.y) / 2;
      n.x = cx2 - len2 / 2;
      n.y = cy2 - edge.width / 2;
      n.w = len2;
      n.h = edge.width;
      n.rotation = ang2;
    };
    addNode(scene, node); z.v++;
  }

  // ------- HUD overlay -----------------------------------------------------
  // A small "title" + "instructions" plate at the top-left. The text is
  // the only thing that doesn't use mix-blend-mode: screen so it stays
  // legible against the busy background.
  addNode(scene, makeRect({
    x: 24, y: 24, w: 360, h: 96,
    radius: 14,
    fill: { r: 0, g: 0, b: 0, a: 0.4 },
    borderWidths: [1, 1, 1, 1],
    borderColor: { r: 1, g: 1, b: 1, a: 0.12 },
    backdropBlur: 14,
  })); z.v++;
  addNode(scene, makeText({
    x: 38, y: 36, w: 340, h: 18,
    text: "CONSTELLATION",
    fontSize: 13,
    fontWeight: 800,
    textColor: { r: 1, g: 1, b: 1, a: 0.95 },
    letterSpacing: 0.32,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z.v++;
  addNode(scene, makeText({
    x: 38, y: 60, w: 340, h: 14,
    text: "180 stars · screen blend · gradient · velocity drift",
    fontSize: 10,
    fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 0.65 },
    letterSpacing: 0.08,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z.v++;
  addNode(scene, makeText({
    x: 38, y: 82, w: 340, h: 14,
    text: "click the Edit toggle to select and drag stars · ↺ to reset",
    fontSize: 10,
    fontWeight: 500,
    textColor: { r: 1, g: 1, b: 1, a: 0.45 },
    letterSpacing: 0.04,
    fontFamily: "system-ui, -apple-system, sans-serif",
  })); z.v++;

  // Final node count audit (debug). Exposed on the scene via onPointerDown
  // so it's reachable from the dev console without polluting the DOM.
  scene.onPointerDown = (px: number, py: number): boolean => {
    // Don't swallow clicks here — let the studio/edit system handle them.
    return false;
  };

  return scene;
}
